import { useState, useEffect } from "react";
import { X, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { isDateCompleted } from "@/utils/gameProgress";
import { cn } from "@/lib/utils";

interface StreakWarningBannerProps {
  currentStreak: number;
}

export function StreakWarningBanner({ currentStreak }: StreakWarningBannerProps) {
  const [dismissed, setDismissed] = useState(false);
  const [hoursRemaining, setHoursRemaining] = useState<number>(24);
  const [minutesRemaining, setMinutesRemaining] = useState<number>(0);

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const now = new Date();
      const midnight = new Date(now);
      midnight.setHours(24, 0, 0, 0);
      const msRemaining = midnight.getTime() - now.getTime();
      setHoursRemaining(Math.floor(msRemaining / (1000 * 60 * 60)));
      setMinutesRemaining(Math.floor((msRemaining % (1000 * 60 * 60)) / (1000 * 60)));
    };

    calculateTimeRemaining();
    
    // Update every minute
    const interval = setInterval(calculateTimeRemaining, 60000);
    return () => clearInterval(interval);
  }, []);

  // Don't show if: dismissed, no streak, or puzzle already completed
  const todayCompleted = isDateCompleted(new Date());
  if (dismissed || currentStreak === 0 || todayCompleted) return null;

  // Urgency levels
  const isUrgent = hoursRemaining < 2;
  const isWarning = hoursRemaining < 6;

  const timeText = hoursRemaining > 0 
    ? `${hoursRemaining}h ${minutesRemaining}m` 
    : `${minutesRemaining}m`;

  return (
    <div 
      className={cn(
        "flex items-center gap-2 px-3 py-2 rounded-lg text-sm animate-fade-in w-full max-w-md mx-auto",
        isUrgent ? "bg-destructive/15 border border-destructive/40 text-destructive" :
        isWarning ? "bg-tile-present/15 border border-tile-present/40 text-tile-present-foreground" :
        "bg-muted border border-border text-muted-foreground"
      )}
    >
      <Flame className={cn(
        "h-4 w-4 shrink-0",
        isUrgent ? "text-destructive animate-pulse" : 
        isWarning ? "text-tile-present" : 
        "text-muted-foreground"
      )} />
      <span className="flex-1 text-center">
        {isUrgent 
          ? `⚠️ Only ${timeText} left to save your ${currentStreak}-day streak!`
          : `Play today to keep your ${currentStreak}-day streak! (${timeText} left)`
        }
      </span>
      <Button 
        variant="ghost" 
        size="icon" 
        className="h-6 w-6 shrink-0 hover:bg-transparent opacity-60 hover:opacity-100" 
        onClick={() => setDismissed(true)}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
